/**
 * RollReviewToolsGate — Wraps any Roll Review tool surface (instructor inbox,
 * setup pages, etc.) and intercepts Free instructors with the premium trigger sheet.
 *
 * Spec SOS-332 rule 2 + Edge Case 5:
 *   - Free instructor (gate denies) → render `fallback` (placeholder) + open sheet
 *   - Active Pro / past_due (within window) / canceled_active → render children
 *   - Expired → render `fallback` + open sheet with reactivation copy
 *
 * Why a wrapper component (not just a hook): forcing every Roll Review entry
 * point to opt in via `<RollReviewToolsGate>...</RollReviewToolsGate>` makes
 * the gate impossible to bypass with new routes added later.
 *
 * Parameterization:
 *   - `featureKey` defaults to 'roll_review.create' but is overridable.
 *   - `fallback` is the visible state while the sheet is shown.
 *   - `origin` lets analytics differentiate sub-surfaces (inbox vs setup).
 *
 * Loading state shows children optimistically — flicker-avoidance.
 * Auto-opens the sheet only once per mount.
 */

import React, { useEffect, useState } from 'react';
import { useFeatureGate } from '../hooks/useFeatureGate';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { PremiumTriggerSheet } from './PremiumTriggerSheet';
import type { PremiumTriggerOrigin } from '../hooks/usePremiumTriggerAnalytics';

interface RollReviewToolsGateProps {
  /** Feature key to check; defaults to roll_review.create. */
  featureKey?: string;
  /** Surface to track in analytics ('roll_review_block' is the spec default). */
  origin?: PremiumTriggerOrigin;
  /**
   * Rendered while the sheet is open (and on dismiss).
   * Should NOT contain the real Roll Review UI — keep it as a placeholder
   * so the user can't continue using the tool without subscribing.
   */
  fallback: React.ReactNode;
  children: React.ReactNode;
}

export const RollReviewToolsGate: React.FC<RollReviewToolsGateProps> = ({
  featureKey = 'roll_review.create',
  origin = 'roll_review_block',
  fallback,
  children,
}) => {
  const { canUse, isLoading } = useFeatureGate(featureKey);
  const { displayState } = useSubscriptionStatus();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  const blocked = !isLoading && !canUse;
  const isReactivation = displayState === 'expired';

  // Auto-open the sheet exactly once when the gate first resolves as blocked.
  useEffect(() => {
    if (blocked && !autoOpened) {
      setSheetOpen(true);
      setAutoOpened(true);
    }
  }, [blocked, autoOpened]);

  if (isLoading) return <>{children}</>;
  if (!blocked) return <>{children}</>;

  return (
    <>
      {fallback}
      <PremiumTriggerSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        origin={origin}
        isReactivation={isReactivation}
      />
    </>
  );
};
